import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './DaysAvailable.scss';
import { DaysAvailableStore } from '../../store/DaysAvailable';
import { observer } from 'mobx-react';
import { RemaniningDays, TypePageDaysAvailable } from '../../types';
import '../profile/balance/balanceStyles.scss';
import { ReactComponent as LocationIcon } from '@iba-team/portal-react-components/dist/svg/outline/location.svg';
import { ReactComponent as CalendarIcon } from '@iba-team/portal-react-components/dist/svg/outline/calendar.svg';
import { ReactComponent as InfoIcon } from '@iba-team/portal-react-components/dist/svg/outline/information.svg';
import { ReactComponent as PeriodIcon } from '@iba-team/portal-react-components/dist/svg/outline/case.svg';
import { ReactComponent as DotsIcon } from '../../assets/icons/dots-vertical.svg';
import { ReactComponent as CompensatoryIcon } from '../../assets/icons/leaveTypes/compensatory-off.svg';
import { ReactComponent as EditIcon } from '@iba-team/portal-react-components/dist/svg/outline/edit.svg';
import { ReactComponent as HistoryIcon } from '@iba-team/portal-react-components/dist/svg/outline/clock.svg';
import {
  Divider,
  Popover,
  Layout, Tooltip,
  EmptyState
} from '@iba-team/portal-react-components/dist';
import { Dropdown } from 'antd';
import { TooltipPlacement } from 'antd/es/tooltip';
import { buildAllowanceModal, buildCompensatoryModal, buildHistoryModal, buildTransferredModal } from './settings';


type PeriodProps = {
  color?: 'positive' | 'negative' ,
  value: number,
  description: string,
  hasTransferDays: boolean,
  tooltipTitle: React.ReactNode | string,
  tooltipDescription: React.ReactNode,
  placement?: TooltipPlacement
}

type Props = {
  userId : number,
  isUserDataValid: boolean,
  className?: string,
  activePage: TypePageDaysAvailable
}


const PeriodElement = (props: PeriodProps) => {
  return (
    <Popover align={{
      overflow: { adjustX: false, adjustY: false },
    }} overlayClassName={'days-available-popover'}
    placement={props.placement} title={props.tooltipTitle} content={props.tooltipDescription}>
      <div className={`days-available__content__item ${props.hasTransferDays ? 'column_item' : 'row_item'} `}>
        <div className={`days-available__content__item__value ${props.color ?? 'default'}`}>
          {props.value}
        </div>
        <div className={'days-available__content__item__description'}>
          {props.description}
        </div>
      </div>
    </Popover>
  );
};

const DaysAvailable = observer((props: Props) => {
  const { t } = useTranslation();
  const { profileRemainingDays, fetchProfileRemainingDays, isFetchedProfileRemainingDays } = DaysAvailableStore;
  const { takencompensatoryOffBalance, compensatoryOffBalance, possibleToEditCBalance } = profileRemainingDays;
  const [open, setOpen] = useState(false);

  useEffect(()=>{
    if(props.userId){
      fetchProfileRemainingDays({userId: props.userId});
    }
  },[props.userId]);

  const hasTransferDays = (profileRemainingDays?.transfer > 0);
  const isYearPeriod = (profileRemainingDays.periodType === 'START_YEAR');

  buildAllowanceModal.useIt();
  buildTransferredModal.useIt();
  buildHistoryModal.useIt();
  buildCompensatoryModal.useIt();

  const availableForNowContent = (action: RemaniningDays) => {
    if(action.availableForNow >= 0 && !action.staticTranferDays){
      return {
        title: t('availableDays.availableForNowTooltipTitle'),
        description: <>{ t('availableDays.availableForNowDescr')}</>
      };
    }
    return {
      title: t('availableDays.availableForNowTooltipTitle'),
      description: <>
        <div>{ t('availableDays.availableForNowDescr') }</div>
        <ul>
          <li>{ t('availableDays.availableForNowTooltipTitle') }:  <span>{action.availableForNowWithoutTransfer}</span></li>
          {action.staticTranferDays !== 0 && <li>
            Transferred from previous period: <span>{action.staticTranferDays}</span>
          </li>
          }
          {action.availableForNow < 0 && <li>Over limit: <span>{action.availableForNow}</span></li>}
        </ul>
      </>
    };

  };

  const availableForThisPeriodContent = (action: RemaniningDays) => {
    if(action.transfer >= 0 && action.available >= 0){
      return {
        title: t('availableDays.availableInThisPeriodTooltipTitle'),
        description: <div>{action.periodType === 'START_YEAR' ? t('availableDays.availableForThisCalendarPeriodDescr') : t('availableDays.availableForThisWorkingPeriodDescr') }</div>
      };
    }
    return {
      title: t('availableDays.availableInThisPeriodTooltipTitle'),
      description: <>
        <div>{action.periodType === 'START_YEAR' ? t('availableDays.availableForThisCalendarPeriodDescr') : t('availableDays.availableForThisWorkingPeriodDescr')}</div>
        <ul>
          <li>
            Allowance: <span>{action.allowanceString}</span>
          </li>
          {action.staticTranferDays !== 0 && <li>
            { t('availableDays.transferredFromPrevPeriodTooltipTitle') }: <span>{action.staticTranferDays}</span>
          </li>}
          {(action.available < 0 && action.staticTranferDays !== 0) && <li>Over limit: <span>{action.available}</span></li>}
        </ul>
      </>
    };

  };

  const transferredFromPrevPeriodContent = (action: RemaniningDays) => {
    return {
      title: t('availableDays.transferredFromPrevPeriodTooltipTitle'),
      description: <div>{action.periodType === 'START_YEAR' ? t('availableDays.transferredFromPreviousCalendarPeriodDescr') : t('availableDays.transferredFromPreviousWorkingPeriodDescr')}</div>
    };
  };

  const daysUsedContent = (action: RemaniningDays) => {
    return {
      title: t('availableDays.daysUsedTooltipTitle'),
      description: <>
        <div>{ t('availableDays.daysUsedDescr') }</div>
        <ul>
          <li>current {action.periodType === 'START_YEAR' ? 'calendar' : 'working'} period: <span>{action.usedInThisPeriod}</span></li>
          {action.usedInTransferDays > 0 && <li> { t('availableDays.restOfPastPeriod')} <span>{action.usedInTransferDays}</span> </li>}
          {action.usedInFuturePeriod > 0 && <li> future {action.periodType === 'START_YEAR' ? 'calendar' : 'working'} period: <span>{action.usedInFuturePeriod}</span> </li>}
        </ul>
      </>
    };

  };

  const getBalanceType = () => {
    if (profileRemainingDays.availableForNow === 0) {
      return undefined;
    }
    return (profileRemainingDays.availableForNow > 0) ? 'positive' : 'negative';
  };

  const getCountDaysEdit = (isLink:boolean,text:string, compensatoryOffBalance:number) => {
    return (<span className={isLink ? 'linkToHelp': 'defaultView'} style={{display: 'flex', alignItems: 'center'}}>
      {`${compensatoryOffBalance} ${text}`}
      {isLink && <EditIcon style={{marginLeft: '4px', cursor: 'pointer'}}/>}
    </span>);
  };

  const chooseCompensatoryOff = () => {
    const isDefaultCompensatory = (takencompensatoryOffBalance === 0 && compensatoryOffBalance === 0);
    const isOneDayOfCompensatory = (compensatoryOffBalance <= 1);
    const isProfileType = (props.activePage === TypePageDaysAvailable.PROFILE);

    if (isDefaultCompensatory) {
      return (
        <>
          <span className="days-available__header__item__wrapper__value">{t('availableDays.compensatoryOffTypes.wasNotChange')}</span>
          {isProfileType && possibleToEditCBalance && <EditIcon onClick={() => buildCompensatoryModal.showImmediately({userId: handleUserId})} style={{cursor: 'pointer', marginLeft: '4px'}} />}
        </>);
    }

    return (
      <>
        <span  className="days-available__header__item__wrapper__value">
          {t('availableDays.compensatoryOffTypes.wasChange', { usedValue: takencompensatoryOffBalance })}
        </span>
        <span
          onClick={() => isProfileType && possibleToEditCBalance &&  buildCompensatoryModal.showImmediately({userId: handleUserId})}
          className={`${possibleToEditCBalance ? 'viewLink' : 'defaultView '} days-available__header__item__wrapper__value`}>
          {isOneDayOfCompensatory ?
            <>{getCountDaysEdit((isProfileType && possibleToEditCBalance), t('availableDays.countDays.oneDay'), compensatoryOffBalance)}</> :
            <>{getCountDaysEdit((isProfileType && possibleToEditCBalance), t('availableDays.countDays.manyDays'), compensatoryOffBalance)}</>
          }
        </span>
      </>);
  };


  const handleUserId = props.userId ? +props.userId : 0;
  const prepareSettingActions = useMemo(() => {
    const res = [];

    if(profileRemainingDays.isPossibleToEditEmployeeBalance){
      res.push(  <div onClick={() => buildAllowanceModal.showImmediately({allowance: profileRemainingDays.employeeAllowanceBalance, userId: handleUserId})}>
        <EditIcon/>
        <p>{t('availableDays.dropdownActionsNames.allowance')}</p>
      </div>,
      <div onClick={() => buildTransferredModal.showImmediately({transferredDays: profileRemainingDays.employeeTransferDaysBalance, userId: handleUserId})}>
        <EditIcon/>
        <p>{t('availableDays.dropdownActionsNames.transferred')}</p>
      </div>,);
    }
    if(profileRemainingDays.isPossibleToViewEmployeeBalance){
      res.push(<div className={profileRemainingDays.isPossibleToEditEmployeeBalance ? 'dropdown-history' : ''}
        onClick={() => buildHistoryModal.showImmediately({userId: handleUserId})}>
        <HistoryIcon/>
        <p>{t('availableDays.dropdownActionsNames.history')}</p>
      </div>);
    }
    return res;
  },[profileRemainingDays, handleUserId]);

  return (
    <>
      <Layout className={`days-available ${props.className}`} allowBaseMargins isLoading={props.isUserDataValid && !isFetchedProfileRemainingDays} >
        <div className="days-available__header">
          <div className={'days-available-header-data'}>
            <div className="days-available__header__item">
              <LocationIcon/>
              <div className="days-available__header__item__value">{profileRemainingDays?.countryName}</div>
            </div>
            {props.isUserDataValid &&
            <>
              <div className="days-available__header__item">
                <PeriodIcon/>
                <div className={'days-available__header__item__wrapper'}>
                  <div className="days-available__header__item__wrapper__description">{t(!isYearPeriod ? 'workingPeriod' : 'calendarPeriod')}:</div>
                  <div className="days-available__header__item__wrapper__value">{profileRemainingDays?.periodString}</div>
                </div>
              </div>
              <div className="days-available__header__item">
                <CalendarIcon/>
                <div className={'days-available__header__item__wrapper'}>
                  <div className="days-available__header__item__wrapper__description">Allowance:</div>
                  <div className="days-available__header__item__wrapper__value last">{profileRemainingDays?.allowanceString}</div>
                </div>
              </div>
            </>
            }
          </div>
          {props.isUserDataValid &&
              <>
                {profileRemainingDays.isPossibleToEditEmployeeBalance ?
                  <Dropdown open={open} onOpenChange={(value) => setOpen(value)} overlayClassName={'days-available-actions-dropdown'}
                    trigger={['click']}
                    overlay={<>{prepareSettingActions.map(action => action)}</>} >
                    <div className={`settingsIconWrapper ${open && 'settingsIconWrapperBlur'}`}>
                      <DotsIcon className={'settingsIcon'}/>
                    </div>
                  </Dropdown>
                  :
                  <Tooltip title={t('availableDays.viewHistory')}>
                    <InfoIcon style={{cursor: 'pointer', width: '20px', height: '20px'}}
                      onClick={() => buildHistoryModal.showImmediately({userId: handleUserId})}/>
                  </Tooltip>
                }
              </>
          }
          
        </div>
        <Divider className={'days-available__divider'}/>
        {props.isUserDataValid ?
          <>
            <div className={`days-available__content ${!hasTransferDays && 'between-content'}`}>
              <PeriodElement hasTransferDays={hasTransferDays}
                color={getBalanceType()}
                placement={'right'}
                tooltipTitle={availableForNowContent(profileRemainingDays).title}
                tooltipDescription={availableForNowContent(profileRemainingDays).description}
                value={profileRemainingDays.availableForNow}
                description={t('availableDays.availableForNow')}/>
              <PeriodElement hasTransferDays={hasTransferDays}
                tooltipTitle={availableForThisPeriodContent(profileRemainingDays).title}
                tooltipDescription={availableForThisPeriodContent(profileRemainingDays).description}
                value={profileRemainingDays.available}
                placement={'bottomLeft'}
                description={t('availableDays.availableInWorkingPeriod')}/>

              {hasTransferDays && <PeriodElement
                value={profileRemainingDays.transfer}
                tooltipTitle={transferredFromPrevPeriodContent(profileRemainingDays).title}
                tooltipDescription={transferredFromPrevPeriodContent(profileRemainingDays).description}
                hasTransferDays={hasTransferDays}
                placement={'bottomRight'}
                description={t('availableDays.remainingDays')}/>}

              <PeriodElement value={profileRemainingDays.taken}
                hasTransferDays={hasTransferDays}
                placement={'left'}
                tooltipTitle={daysUsedContent(profileRemainingDays).title}
                tooltipDescription={daysUsedContent(profileRemainingDays).description}
                description={t('availableDays.usedDays')}/>
            </div>
            <Divider className={'days-available__divider'}/>
            <div className={'days-available__header__item__wrapper'}>
              <CompensatoryIcon/>
              <span  className="days-available__header__item__wrapper__description">
                {t('compensatoryOff')}:
              </span>
              {chooseCompensatoryOff()}
            </div>
          </>
          :
          <EmptyState
            className={'empty'}
            title={t('messagesForBanners.noData.title')}
            description={t('messagesForBanners.noData.body')}
          />
        }
      </Layout>
    </>
  );
});

export default DaysAvailable;
